import React, { FC, useCallback } from "react";

import {
  PaymentStatuses,
  type Resale,
  type ResaleFragment,
  SortDirection,
  useListGroupedResalesQuery,
} from "@api/graphql";
import { GroupedResales } from "@components/grouped_resales/GroupedResales";
import { ResalesAccordionHeader } from "@components/grouped_resales/ResaleAccordionHeader";
import { useIsAuthUserAllowed } from "@components/permission_control/ProtectedComponent";
import type { TableHeader } from "@components/table/TableContents";
import type { TableFilterInput } from "@components/table/TableFilter";
import { ExternalRoles, InternalRoles } from "@roles";
import type { FilterByTyped, SortByTyped } from "@util/index";
import type { ResaleGroup } from "@util/resale";

const pastResaleReportTableHeaders: TableHeader<ResaleFragment>[] = [
  { label: "ID", accessor: "resale_id" },
  { label: "Artwork title", accessor: "artwork_name" },
  { label: "Artist", accessor: "artist_name" },
  { label: "Sold date", accessor: "date_sold" },
  { label: "Resale price", accessor: "resale_price" },
  { label: "Royalty", accessor: "royalty" },
  { label: "Status", accessor: "payment_status" },
];

export const PastResaleReports: FC<{ displayBanner?: boolean; vendorId?: number }> = ({ displayBanner, vendorId }) => {
  const isExternalUser = useIsAuthUserAllowed(ExternalRoles);
  const isInternalUser = useIsAuthUserAllowed(InternalRoles);

  const sort_by: SortByTyped<Resale> = {
    field: "reported_on",
    direction: SortDirection.DESCENDING,
  };

  const paymentStatusFilterOptions: TableFilterInput[] = [
    { option: PaymentStatuses.AMP_INVOICE_PAID, defaultValue: true },
    { option: PaymentStatuses.AMP_INVOICE_REQUESTED, defaultValue: true },
    { option: PaymentStatuses.ARTIST_PAID, defaultValue: true },
    { option: PaymentStatuses.ARTIST_PAYMENT_REQUESTED, defaultValue: true },
    { option: PaymentStatuses.CREDIT_NOTE_ISSUED, defaultValue: true },
    { option: PaymentStatuses.CREDIT_NOTE_REQUESTED, defaultValue: true },
    { option: PaymentStatuses.NOT_APPLICABLE, defaultValue: false },
  ];

  const filter_by: FilterByTyped<Resale>[] = [
    {
      field: "payment_status",
      in: paymentStatusFilterOptions.map(({ option }) => option),
    },
  ];

  if (isInternalUser && vendorId) {
    filter_by.push({ field: "vendor_id", eq: vendorId.toString() });
  }

  const getAccordionHeader = useCallback(
    ({ resales }: ResaleGroup) => {
      return <ResalesAccordionHeader resales={resales} isExternalUser={isExternalUser} resaleCount={resales.length} />;
    },
    [isExternalUser],
  );

  return (
    <GroupedResales
      queryFunction={useListGroupedResalesQuery}
      getQueryVariables={(page) => ({
        input: { page, limit: 10, filter_by, sort_by },
      })}
      updateQueryVariablesWithFilter={({ input: { page, limit, filter_by, sort_by } }, new_filter_by) => ({
        input: {
          page,
          limit,
          filter_by: filter_by.concat(new_filter_by ? new_filter_by : []),
          sort_by,
        },
      })}
      getResaleGroupsFromQueryData={(data) => data.listGroupedResales.results}
      getPaginatedOutputFromQueryData={(data) => data.listGroupedResales}
      getAccordionHeader={getAccordionHeader}
      customTableHeaders={pastResaleReportTableHeaders}
      paymentStatusFilterOptions={paymentStatusFilterOptions}
      sourcePage={"PastResaleReports"}
      displayBanner={displayBanner}
    />
  );
};
